import { useState, useEffect, useCallback, useTransition } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { listIssuedItems, deleteIssueTransaction, fetchIssueOptions } from '../api/issueItemApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { useExportJob } from '../../../../hooks/useExportJob'
import { executePrintReport } from '../../../../utils/printReportUtil'

export function useIssueItemListScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fromStudent = searchParams.get('from') === 'students'
  const [, startTransition] = useTransition()

  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filter States
  const [searchField, setSearchField] = useState('voucherNo')
  const [searchText, setSearchText] = useState('')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [departmentFilter, setDepartmentFilter] = useState('All')
  const [voucherNoFilter, setVoucherNoFilter] = useState('')
  const [datePreset, setDatePreset] = useState('Period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [itemFilter, setItemFilter] = useState('')

  // Dynamic Options
  const [options, setOptions] = useState({
    institutes: [{ label: 'All Institutes', value: 'All' }],
    branches: [{ label: 'All Branches', value: 'All' }],
    departments: [
      { label: 'All Departments', value: 'All' },
      { label: 'Academic', value: 'Academic' },
      { label: 'Administration', value: 'Administration' },
      { label: 'Accounts', value: 'Accounts' },
    ],
    items: [],
  })

  // Export Job Hook
  const { triggerExport, isExporting } = useExportJob({ module: 'issue_item' })

  // Load options on mount
  useEffect(() => {
    fetchIssueOptions()
      .then((data) => {
        if (data) {
          setOptions((prev) => ({
            ...prev,
            institutes: [
              { label: 'All Institutes', value: 'All' },
              ...(data.institutes || []).map((x) => ({ label: x.label, value: x.value })),
            ],
            branches: [
              { label: 'All Branches', value: 'All' },
              ...(data.branches || []).map((x) => ({ label: x.label, value: x.value })),
            ],
            items: data.items || [],
          }))
        }
      })
      .catch(() => {})
  }, [])

  // Load items from backend
  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = {
        page,
        pageSize,
        company: companyFilter !== 'All' ? companyFilter : undefined,
        branch: branchFilter !== 'All' ? branchFilter : undefined,
        department: departmentFilter !== 'All' ? departmentFilter : undefined,
        voucherNo: voucherNoFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        item: itemFilter || undefined,
        searchText: searchText.trim() || undefined,
      }
      const res = await listIssuedItems(params)
      startTransition(() => {
        setRows(res.items || [])
        setTotalCount(res.totalCount || 0)
        setTotalPages(res.totalPages || 1)
      })
    } catch (err) {
      appToast.error(err.response?.data?.detail || err.message || 'Failed to load issued items')
      setRows([])
      setTotalCount(0)
      setTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, companyFilter, branchFilter, departmentFilter, voucherNoFilter, fromDate, toDate, itemFilter, searchText])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handlePeriodChange = (val) => {
    setDatePreset(val)
    const today = new Date()
    const fmt = (d) => d.toISOString().split('T')[0]

    if (val === 'Today') {
      const d = fmt(today)
      setFromDate(d)
      setToDate(d)
    } else if (val === 'Yesterday') {
      const y = new Date(today)
      y.setDate(y.getDate() - 1)
      const d = fmt(y)
      setFromDate(d)
      setToDate(d)
    } else if (val === 'Current Month') {
      const f = new Date(today.getFullYear(), today.getMonth(), 1)
      const t = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      setFromDate(fmt(f))
      setToDate(fmt(t))
    } else if (val === 'Current Year') {
      const f = new Date(today.getFullYear(), 0, 1)
      const t = new Date(today.getFullYear(), 11, 31)
      setFromDate(fmt(f))
      setToDate(fmt(t))
    } else if (val === 'Period') {
      setFromDate('')
      setToDate('')
    }
  }

  const resetFilters = () => {
    setSearchText('')
    setCompanyFilter('All')
    setBranchFilter('All')
    setDepartmentFilter('All')
    setVoucherNoFilter('')
    setDatePreset('Period')
    setFromDate('')
    setToDate('')
    setItemFilter('')
    setPage(1)
  }

  const handleDelete = async (id, voucherNo) => {
    const confirmed = await appConfirm({
      title: 'Delete Issue Transaction',
      message: `Are you sure you want to delete issue transaction ${voucherNo || `#${id}`}? This will restore student item inventory.`,
      confirmText: 'Delete Transaction',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteIssueTransaction(id)
      appToast.success('Transaction deleted successfully')
      loadData()
    } catch (err) {
      appToast.error(err.response?.data?.detail || 'Failed to delete transaction')
    }
  }

  const handleEdit = (id) => {
    navigate(`/admission/issue-item/entry/${id}`)
  }

  const handleExport = () => {
    triggerExport({
      searchField,
      searchText,
      company: companyFilter === 'All' ? '' : companyFilter,
      branch: branchFilter === 'All' ? '' : branchFilter,
      department: departmentFilter === 'All' ? '' : departmentFilter,
      voucherNo: voucherNoFilter,
      fromDate,
      toDate,
      item: itemFilter,
    })
  }

  const handlePrint = () => {
    executePrintReport({
      title: 'List of Issued Items',
      data: rows,
      columns: [
        { label: 'vDate', key: 'voucherDate' },
        { label: 'vNo', key: 'voucherNo' },
        { label: 'Branch', key: 'branch' },
        { label: 'Item', key: 'item' },
        { label: 'Qty', key: 'quantity' },
        { label: 'Students', key: 'studentCount' },
        { label: 'Status', key: 'status' },
      ],
    })
  }

  return {
    state: {
      isLoading,
      rows,
      totalCount,
      totalPages,
      page,
      pageSize,
      searchField,
      searchText,
      companyFilter,
      branchFilter,
      departmentFilter,
      voucherNoFilter,
      datePreset,
      fromDate,
      toDate,
      itemFilter,
      options,
      fromStudent,
      isExporting,
    },
    actions: {
      setPage,
      setPageSize,
      setSearchField,
      setSearchText,
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setVoucherNoFilter,
      setDatePreset,
      setFromDate,
      setToDate,
      setItemFilter,
      handlePeriodChange,
      handleSearch: loadData,
      resetFilters,
      handleDelete,
      handleEdit,
      handleExport,
      handlePrint,
    },
  }
}
